/**
 * SubmittedTicketRenderer — adapter that lets the approval renderer
 * registry dispatch to our bespoke SubmittedTicketCard.
 *
 * Same shape as DraftTicketRenderer: validates and falls back to an
 * inline error if the payload isn't well-formed.
 */

import type { ReactNode } from 'react'
import { SubmittedTicketCard } from '../components/SubmittedTicketCard'
import type { SubmittedTicketPayload } from '../types'

interface RendererProps {
  data: unknown
}

function isSubmittedPayload(value: unknown): value is SubmittedTicketPayload {
  if (!value || typeof value !== 'object') return false
  const obj = value as Record<string, unknown>
  return (
    typeof obj.ticket_id === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.description === 'string' &&
    typeof obj.entity_id === 'string' &&
    typeof obj.type === 'string' &&
    typeof obj.severity === 'string'
  )
}

export function SubmittedTicketRenderer({ data }: RendererProps): ReactNode {
  if (!isSubmittedPayload(data)) {
    return (
      <div role="alert" style={errorStyle}>
        Submitted ticket data is missing or malformed.
      </div>
    )
  }
  return <SubmittedTicketCard data={data} variant="compact" />
}

const errorStyle = {
  padding: '10px 12px',
  fontSize: '12px',
  color: 'var(--error)',
  border: '1px solid rgba(239, 68, 68, 0.3)',
  background: 'rgba(239, 68, 68, 0.08)',
  borderRadius: 'var(--radius-sm)',
}

export default SubmittedTicketRenderer
